import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { BinariesService } from '@services/binaries/binaries.service';
import { NotificationService } from '@services/notification.service';
import { BinaryLite } from '@shared/models/binaries/binary-lite.model';
import { DataRefsByKey, ApplianceGroup } from '@shared/models/dataref.model';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { merge, Observable, of, Subject } from 'rxjs';
import { mergeMap, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'app-binaries-list',
  templateUrl: './binaries-list.component.html',
  styleUrls: ['./binaries-list.component.scss'],
  standalone: false,
})
export class BinariesListComponent implements OnInit {
  @Input() recipeOriginal: RecipeLite = {} as RecipeLite;
  @Input() shouldReload$: EventEmitter<void> = new EventEmitter<void>();
  @Input() applianceGroupsRefData: DataRefsByKey<ApplianceGroup> = {};

  binaries$: Observable<BinaryLite[]> = of([]);

  publishBinary$ = new Subject<BinaryLite>();
  deleteBinary$ = new Subject<BinaryLite>();

  readonly displayedColumns: string[] = ['applianceGroup', 'type', 'creationDate', 'createdBy', 'status', 'actions'];

  constructor(
    private readonly binariesService: BinariesService,
    private readonly notificationsService: NotificationService,
  ) {}

  ngOnInit(): void {
    if (!this.recipeOriginal) {
      return;
    }

    const binaryPublication$ = this.publishBinary$.pipe(
      mergeMap((binary) =>
        this.binariesService.publishForRecipe({
          recipeId: this.recipeOriginal.identifier,
          binaryId: binary.identifier,
          groupingId: this.recipeOriginal.groupingId,
        }),
      ),
      tap(() => this.notificationsService.sendSuccess('BINARIES.UPDATED')),
    );

    const binaryDeletion$ = this.deleteBinary$.pipe(
      mergeMap((binary) =>
        this.binariesService.deleteForRecipe({
          recipeId: this.recipeOriginal.identifier,
          binaryId: binary.identifier,
          groupingId: this.recipeOriginal.groupingId,
        }),
      ),
      tap(() => this.notificationsService.sendSuccess('BINARIES.DELETED')),
    );

    this.binaries$ = merge(this.shouldReload$, binaryPublication$, binaryDeletion$).pipe(
      mergeMap(() =>
        this.binariesService.getListForRecipe({
          recipeId: this.recipeOriginal.identifier,
          groupingId: this.recipeOriginal.groupingId,
        }),
      ),
      tap((reloadedBinaries) => (this.recipeOriginal.binaries = reloadedBinaries)),
      startWith(this.recipeOriginal.binaries),
    );
  }
}
